import { useEffect, useState } from 'react';
import TagCreator from '../TagCreator';
import { formatTime, parseTime } from '../../lib/videoQuiz';

export interface CfTag { id: number; name: string; color: string }

/** `m:ss.s` time field. Commits on blur / Enter; invalid input reverts. */
export function TimeInput({
  value, onChange, className = '', placeholder = '0:00.0', ariaLabel,
}: {
  value: number | undefined;
  onChange: (sec: number) => void;
  className?: string;
  placeholder?: string;
  ariaLabel?: string;
}) {
  const [text, setText] = useState(formatTime(value));
  const [invalid, setInvalid] = useState(false);
  useEffect(() => { setText(formatTime(value)); setInvalid(false); }, [value]);

  const commit = () => {
    const parsed = parseTime(text);
    if (parsed === null) {
      setInvalid(text.trim() !== '');
      if (text.trim() === '') setText(formatTime(value));
      return;
    }
    setInvalid(false);
    if (parsed !== value) onChange(parsed);
    else setText(formatTime(value));
  };

  return (
    <input
      type="text"
      inputMode="decimal"
      value={text}
      aria-label={ariaLabel}
      onChange={(e) => { setText(e.target.value); setInvalid(false); }}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit(); } }}
      placeholder={placeholder}
      title="Minutes:seconds.tenths, e.g. 1:05.5"
      className={`ds-input font-mono w-24 ${invalid ? '!border-danger' : ''} ${className}`}
    />
  );
}

const TAG_LIST_MAX = 24;

/** Chip-style multi tag picker (same look as the per-result tag picker), optionally with the inline TagCreator. */
export function TagChipPicker({
  tags, selected, onChange, connectionId, onTagCreated, emptyText = 'No tags yet — add one in the Lead Capture tab.',
}: {
  tags: CfTag[];
  selected: string[];
  onChange: (next: string[]) => void;
  connectionId?: string;
  onTagCreated?: (tag: CfTag) => void;
  emptyText?: string;
}) {
  const [query, setQuery] = useState('');
  const sorted = [...tags].sort((a, b) => a.name.localeCompare(b.name));
  // Workspaces often have hundreds of tags: always show the selected ones, and only list the
  // rest through a search (or all of them when the list is short).
  const q = query.trim().toLowerCase();
  const chosen = sorted.filter((t) => selected.includes(String(t.id)));
  const others = sorted.filter((t) => !selected.includes(String(t.id)) && (!q || t.name.toLowerCase().includes(q)));
  const listOthers = q || sorted.length <= TAG_LIST_MAX;
  const visible = [...chosen, ...(listOthers ? others.slice(0, 60) : [])];
  return (
    <div>
      {sorted.length > TAG_LIST_MAX && (
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={`Search ${sorted.length} tags…`}
          className="ds-input !py-1 text-xs mb-2 w-full max-w-xs"
        />
      )}
      {sorted.length === 0 ? (
        <p className="text-xs text-ink-3">{emptyText}</p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {visible.length === 0 && <span className="text-xs text-ink-3">{q ? 'No tags match.' : 'None selected — search to add tags.'}</span>}
          {visible.map((t) => {
            const ts = String(t.id);
            const on = selected.includes(ts);
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => onChange(on ? selected.filter((x) => x !== ts) : [...selected, ts])}
                className={`px-2 py-1 rounded text-xs font-medium border transition-colors ${on ? 'bg-accent-soft border-accent text-accent-ink' : 'bg-surface border-line text-ink-3 hover:border-ink-3'}`}
              >
                {t.name}
              </button>
            );
          })}
        </div>
      )}
      {connectionId && onTagCreated && (
        <TagCreator
          connectionId={connectionId}
          onCreated={(tag) => {
            onTagCreated(tag);
            const ts = String(tag.id);
            if (!selected.includes(ts)) onChange([...selected, ts]);
          }}
        />
      )}
    </div>
  );
}

/** Small on/off switch matching the publish toggle in the quiz list. */
export function Switch({ checked, onChange, label, id }: { checked: boolean; onChange: (v: boolean) => void; label?: string; id?: string }) {
  return (
    <button
      type="button"
      id={id}
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className="relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors"
      style={{ background: checked ? 'var(--accent)' : 'var(--ink-3)' }}
    >
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${checked ? 'translate-x-6' : 'translate-x-1'}`} />
    </button>
  );
}
